import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getLabCompareWorkbench } from '../api/smartiLabApi';
import LabCard from '../components/LabCard';
import LabEmptyState from '../components/LabEmptyState';
import LabMetric from '../components/LabMetric';
import SmartiLabBadge from '../components/SmartiLabBadge';

export default function CompareWorkbenchPage() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const mode = searchParams.get('mode') || 'suspect';
  const left = searchParams.get('left') || '';
  const right = searchParams.get('right') || '';

  useEffect(() => {
    getLabCompareWorkbench({ mode, left, right }).then(setData).catch((loadError) => setError(loadError.message || 'Unable to load compare mode.'));
  }, [left, mode, right]);

  const cards = useMemo(() => {
    if (!data) return [];
    return [data.left, data.right].filter(Boolean);
  }, [data]);

  return (
    <div className="lab-page">
      <LabCard title="Compare mode" description="Side-by-side comparison for suspects, sessions, or stations. Start from a cockpit action, then add the second item from the URL or another page.">
        {error ? <div className="lab-error-text">{error}</div> : null}
        {!cards.length ? (
          <LabEmptyState title="No compare selection yet" description="Open compare from a suspect or session action, then optionally add a second target in the URL." />
        ) : (
          <>
            <div className="lab-metric-grid">
              <LabMetric label="Mode" value={data.mode || mode} />
              <LabMetric label="Loaded cards" value={cards.length} />
            </div>
            <div className="lab-compare-grid">
              {cards.map((item, index) => (
                <article key={`${data.mode}-${index}`} className="lab-compare-card">
                  <div className="lab-chip-row">
                    <SmartiLabBadge variant="primary" label={data.mode === 'session' ? `Session ${item.session?.global_id || item.global_id}` : data.mode === 'station' ? `${item.stationName || 'Station'}` : `Candidate ${item.candidate_id || item.stepname || index + 1}`} />
                    <SmartiLabBadge variant="success" label={`Confidence ${Number(item.suspect_confidence || item.randomForestSessionRisk?.rf_failure_probability_calibrated || item.summary?.failRate || 0).toFixed(2)}`} />
                  </div>
                  <h3>{data.mode === 'session' ? item.session?.serialnumber || item.session?.global_id : data.mode === 'station' ? item.stationName : item.stepname || item.stepnumber}</h3>
                  <p>{data.mode === 'session' ? item.session?.station_name_norm || 'Session comparison' : data.mode === 'station' ? (item.recommendations || []).join(' ') : [item.station_name_norm, item.testspec_norm, item.machine_identifier_norm].filter(Boolean).join(' • ')}</p>
                  <div className="lab-metric-grid">
                    <LabMetric label="Priority" value={item.investigation_priority != null ? Number(item.investigation_priority).toFixed(3) : item.summary?.failRate != null ? `${Number(item.summary.failRate).toFixed(2)}% fail rate` : '—'} />
                    <LabMetric label="Evidence" value={item.root_cause_evidence_score != null ? Number(item.root_cause_evidence_score).toFixed(3) : item.summary?.totalTests ?? '—'} />
                    <LabMetric label="Model score" value={item.rf_weak_root_cause_score != null ? Number(item.rf_weak_root_cause_score).toFixed(3) : item.randomForestSessionRisk?.rf_failure_probability_calibrated != null ? Number(item.randomForestSessionRisk.rf_failure_probability_calibrated).toFixed(3) : item.summary?.uniqueSerials ?? '—'} />
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </LabCard>
    </div>
  );
}
